import { Provider, useSelector } from "react-redux";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { ConfigProvider } from "antd";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
  useLocation,
  useParams
} from "react-router-dom";
import i18next from "i18next";
import store from "./store";
import Login from "./modules/login/login";
import en from "./common/locales/en.json";
import UserManagament from "./modules/administration/user-management/user-managament";
import SiteManagament from "./modules/administration/site-management/site-management";
// import axios from "axios";
import PrivateRoutes from "./common/utils/private-routes";
import useSession from "./common/utils/use-session";
import OpenApp from "./modules/open-app/open-app";
import OAuthConsent from "./modules/oauth-consent/oauth-consent";
import AiConnections from "./modules/oauth-consent/ai-connections";
import AiConnectorSettings from "./modules/administration/ai-connectors/ai-connectors";
import ProjectAiConnectors from "./modules/administration/project-ai-connectors/project-ai-connectors";
import ResetPass from "./modules/login/reset-password";
import NewPass from "./modules/login/new-password";
import SubjectManagement from "./modules/subjects-management/subject-management";
import SubjectManagementLayout from "./modules/subjects-management/SubjectManagementLayout";
import SubjectLayout from "./modules/subjects-management/subject/subject-layout";
import Reports from "./modules/reports/reports";
import Dashboard from "./modules/dashboard/dashboard";
import { Administration } from "./modules/administration/administration";
import { ContentPage } from "./modules/administration/contentPage";
import { ContentOverview } from "./modules/administration/content-overview/content-overview";
import TaskManagement from "./modules/administration/task-management/task-management";
import RuleManagement from "./modules/administration/rule-management/rule-management";
import { ArticleManagement } from "./modules/administration/article-management/article-management";
import LifeFacts from "./modules/administration/life-facts/life-facts";
import PlanManagement from "./modules/administration/plan-management/plan-management";
import LifePoints from "./modules/administration/life-points/life-points";
import LifePointsDescription from "./modules/administration/life-points-description/life-points-description";
import CategoryManagement from "./modules/administration/category-management/category-management";
import TagManagement from "./modules/administration/tag-management/tag-management";
import CohortManagement from "./modules/administration/cohort-management/cohort-management";
import ScenarioManagement from "./modules/administration/scenario-management/scenario-management";
import OnboardingManagement from "./modules/administration/onboarding-management/onboarding-management";
import EnrollmentScenario from "./modules/administration/enrollment-scenario/enrollment-scenario";
import UsersTrailTable from "./modules/reports/components/users-table/users-table";
import ChatsTrailTable from "./modules/reports/components/chats-table/chats-table";
import AiCallsTable from "./modules/reports/components/ai-calls-table/ai-calls-table";
import SubjectsTrailTable from "./modules/reports/components/subjects-table/subjects-table";
import ScenariosTrailTable from "./modules/reports/components/scenarios-table/scenarios-table";
import MessagesAnalyticsTable from "./modules/reports/components/messages-analytics-table/messages-analytics-table";
import OnboardingsAnalyticsTable from "./modules/reports/components/onboardings-analytics-table/onboardings-analytics-table";
import TasksAnalyticsTable from "./modules/reports/components/tasks-analytics-table/tasks-analytics-table";
import ArticleEngagementTable from "./modules/reports/components/article-engagement/article-engagement-table";
import UnifiedAiAssistant from "./components/UnifiedAiAssistant/UnifiedAiAssistant";
import LifeBot from "./modules/administration/life-bot/life-bot";
import Functionality from "./modules/administration/functionality/functionality";
import Notifications from "./modules/administration/notifications/notifications";
import { LegalDocumentManagement }  from "./modules/administration/legal-document-management/legal-document-management";
import Profile from "./modules/administration/profile/profile-management";
import ProjectManagement from "./modules/administration/project-management/project-management";
import './api/setupAxios';
import { useSyncProjectFromUrl } from "./common/utils/useSyncProjectFromUrl";
import { LegacyProjectRedirect } from "./common/utils/LegacyProjectRedirect";
import "./App.css";
import Authentication from "./modules/administration/authentication/authentication";
import RolesManagement from "./modules/administration/roles/roles-management";
import MobileHomePage from "./modules/administration/mobile-home-page/mobile-home-page";
import SurveysReport from "./modules/reports/components/surveys-report/surveys-report";
import OnboardingsReport from "./modules/reports/components/onboardings-report/onboardings-report";
import UsageAnalytics from "./modules/reports/components/usage-analytics/usage-analytics";
import NotificationOutbox from "./modules/administration/notification-outbox/notification-outbox";
import PlanningAudit from "./modules/administration/planning-audit/planning-audit";
import ClonedEnrollmentRepair from "./modules/administration/cloned-enrollment-repair/cloned-enrollment-repair";
import TaskGraphMigration from "./modules/administration/task-graph-migration/task-graph-migration";
import SubjectPlanningTools from "./modules/administration/subject-planning-tools/subject-planning-tools";
import AdminTools from "./modules/administration/admin-tools/admin-tools";
import ArticleCoverBackfill from "./modules/administration/article-cover-backfill/article-cover-backfill";
import SurveyReportBackfill from "./modules/administration/survey-report-backfill/survey-report-backfill";
import ProjectCloneDebug from "./modules/administration/project-clone-debug/project-clone-debug";

import { selectIsSystemAdmin } from "./store/slices/user";
import { usePermissionsSync } from "./common/utils/usePermissions";
import {
  AdministrationIndexRedirect,
  AnalyticsIndex,
  ContentIndexRedirect,
  RequireScreen,
} from "./common/permissions/route-guards";

const ProjectLayout = () => {
  useSyncProjectFromUrl();
  // LF360-2024: what the user may do in the project in the URL.
  usePermissionsSync();
  return <Outlet />;
};

// LF360-2037: sites moved from Content to Administration; keep old links working.
const LegacySitesRedirect = () => {
  const { projectCode, "*": rest } = useParams();
  const location = useLocation();
  const tail = rest ? `/${rest}` : "";

  return (
    <Navigate
      to={`/${projectCode}/administration/sites${tail}${location.search}`}
      replace
    />
  );
};

const RequireSystemAdmin = () => {
  const isSystemAdmin = useSelector(selectIsSystemAdmin);
  const location = useLocation();
  const { projectCode } = useParams();

  if (!isSystemAdmin) {
    return (
      <Navigate
        to={`/${projectCode}/dashboard`}
        replace
        state={{ from: location }}
      />
    );
  }

  return <Outlet />;
};


i18next.use(initReactI18next).init({
  lng: "en",
  resources: {
    en: {
      translation: en,
    },
  },
  interpolation: { escapeValue: false },
});

/**
 * LF360-1970 — asks the server whether this browser has a session, once, before
 * anything decides what to render.
 *
 * Renders nothing; it exists to be inside the `Provider`, because the answer
 * goes into the store and `PrivateRoutes` and the login screen both read it
 * from there. The session is an HttpOnly cookie, so this request is the only
 * way the page can find out.
 */
const SessionBoot = () => {
  useSession();

  return null;
};

const App = () => (
  <Provider store={store}>
    <SessionBoot />
    <I18nextProvider i18n={i18next}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#0141E4",
            borderRadius: "5px",
            fontFamily: "Sora",
          },
          components: {
            Select: {
              optionSelectedBg: "var(--shade-color)",
            },
          },
        }}
      >
        <Router>
          <Routes>
            <Route path="/" element={<LegacyProjectRedirect targetPath="/dashboard" />} />
            <Route element={<Login />} path="/login" exact />
            <Route element={<ResetPass />} path="login/reset" />
            <Route element={<NewPass />} path="login/new-password" />

            {/* LF360-1953 — where a reminder email's "Open Application" link
                lands when the phone did not open the app itself: a desktop, or a
                device without it installed. Public, and deliberately outside
                PrivateRoutes: whoever follows that link has no session, and
                sending them to a login screen for the admin portal would be the
                wrong answer to a link about their own tasks. */}
            <Route element={<OpenApp />} path="/app/*" />

            {/* LF360-2100 — an AI assistant (Claude, ChatGPT) sends the user
                here to allow it into LF360. Outside PrivateRoutes and the
                project layout: the page remembers its own address before
                sending a signed-out user to the login screen (PrivateRoutes
                would lose it), and the project is what the user picks on it. */}
            <Route element={<OAuthConsent />} path="/oauth/consent" />
            <Route element={<AiConnections />} path="/oauth/connections" />

            <Route element={<PrivateRoutes />}>
              <Route
                path="/dashboard"
                element={<LegacyProjectRedirect targetPath="/dashboard" />}
              />
              <Route
                path="/administration/*"
                element={<LegacyProjectRedirect />}
              />
              <Route
                path="/subject-management/*"
                element={<LegacyProjectRedirect />}
              />
              <Route
                path="/reports/*"
                element={<LegacyProjectRedirect />}
              />
              <Route path=":projectCode" element={<ProjectLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route element={<Dashboard />} path="dashboard" exact />
                
                <Route path="subject-management" element={<SubjectManagementLayout />}>
                  <Route index element={<SubjectManagement />} />
                  <Route path="pending" element={<SubjectManagement />} />
                  <Route path="disabled" element={<SubjectManagement />} />
                  <Route path="uploads" element={<SubjectManagement />} />

                  <Route path="subject" element={<SubjectLayout />} />
                  <Route path="subject/:id" element={<SubjectLayout />} />

                  <Route path="ai-assistant" element={<UnifiedAiAssistant />} />
                  <Route path="ai-assistant/:sessionId" element={<UnifiedAiAssistant />} />
                </Route>
                <Route element={<UserManagament />} path="user-management" exact />

                {/* ============ ADMINISTRATION ============ */}
                <Route path="administration" element={<Administration />}>
                  <Route index element={<AdministrationIndexRedirect />} />

                  <Route element={<RequireScreen fallback="/administration" />}>
                    <Route
                      path="system-users"
                      element={<UserManagament scope="organization" />}
                    />
                    <Route
                      path="disabled-users"
                      element={<UserManagament scope="organization" status="disabled" />}
                    />
                    <Route
                      path="pending-users"
                      element={<UserManagament scope="organization" status="pending" />}
                    />
                    <Route path="system-users/create" element={<UserManagament mode="create" scope="organization" />} />
                    <Route path="system-users/:id" element={<UserManagament mode="show" scope="organization" />} />
                    <Route path="system-users/:id/edit" element={<UserManagament mode="edit" scope="organization" />} />
                    <Route path="authentication" element={<Authentication />} />
                    <Route path="roles" element={<RolesManagement />} />
                    <Route path="roles/:id" element={<RolesManagement />} />

                    <Route
                      path="project-users"
                      element={<UserManagament scope="project" />}
                    />
                    <Route path="project-users/create" element={<UserManagament mode="create" scope="project" />} />
                    <Route path="project-users/:id" element={<UserManagament mode="show" scope="project" />} />
                    <Route path="project-users/:id/edit" element={<UserManagament mode="edit" scope="project" />} />

                    <Route path="profiles" element={<Profile />} />
                    <Route path="profiles/create" element={<Profile mode="create" />} />
                    <Route path="profiles/:id" element={<Profile mode="show" />} />
                    <Route path="profiles/:id/edit" element={<Profile mode="edit" />} />

                    <Route path="project-settings" element={<ProjectManagement />} />

                    <Route path="LifeBot" element={<LifeBot />} />
                    <Route path="functionality" element={<Functionality />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="lifepoints" element={<LifePointsDescription />} />
                    <Route path="enrollment" element={<EnrollmentScenario />} />
                    <Route path="cohorts" element={<CohortManagement />} />
                    <Route path="legal-documents" element={<LegalDocumentManagement />} />
                    <Route path="legal-documents/:id" element={<LegalDocumentManagement mode="show" />} />
                    <Route path="legal-documents/:id/edit" element={<LegalDocumentManagement mode="edit" />} />
                    <Route path="mobile-home-page" element={<MobileHomePage />} />
                    {/* LF360-2100: AI assistants in this project. */}
                    <Route path="project-ai-connectors" element={<ProjectAiConnectors />} />

                    <Route path="sites" element={<SiteManagament />} />
                    <Route path="sites/create" element={<SiteManagament mode="create" />} />
                    <Route path="sites/:id" element={<SiteManagament mode="show" />} />
                    <Route path="sites/:id/edit" element={<SiteManagament mode="edit" />} />
                  </Route>

                  <Route element={<RequireSystemAdmin />}>
                    {/* LF360-2100: the organization's switch for AI connectors. */}
                    <Route path="ai-connectors" element={<AiConnectorSettings />} />
                    <Route path="tools" element={<AdminTools />} />
                    <Route path="notification-outbox" element={<NotificationOutbox />} />
                    <Route path="planning-audit" element={<PlanningAudit />} />
                    <Route
                      path="cloned-enrollment-repair"
                      element={<ClonedEnrollmentRepair />}
                    />
                    <Route path="subject-planning-tools" element={<SubjectPlanningTools />} />
                    <Route path="project-clones" element={<ProjectCloneDebug />} />
                    <Route path="article-cover-backfill" element={<ArticleCoverBackfill />} />
                    <Route path="survey-report-backfill" element={<SurveyReportBackfill />} />
                    <Route path="task-graph-migration" element={<TaskGraphMigration />} />
                  </Route>
                </Route>

                <Route path="content" element={<ContentPage />}>
                  <Route index element={<ContentIndexRedirect />} />

                  <Route element={<RequireScreen fallback="/dashboard" />}>
                    <Route path="overview" element={<ContentOverview />} />
                    <Route path="tasks" element={<TaskManagement />} />
                    <Route path="tasks/create" element={<TaskManagement mode="create" />} />
                    <Route path="tasks/:id" element={<TaskManagement mode="show" />} />
                    <Route path="tasks/:id/edit" element={<TaskManagement mode="edit" />} />

                    <Route path="articles" element={<ArticleManagement />} />
                    <Route path="articles/create" element={<ArticleManagement mode="create" />} />
                    <Route path="articles/:id" element={<ArticleManagement mode="show" />} />
                    <Route path="articles/:id/edit" element={<ArticleManagement mode="edit" />} />

                    <Route path="fitfacts" element={<LifeFacts />} />
                    <Route path="fitfacts/create" element={<LifeFacts mode="create" />} />
                    <Route path="fitfacts/:id" element={<LifeFacts mode="show" />} />
                    <Route path="fitfacts/:id/edit" element={<LifeFacts mode="edit" />} />

                    <Route path="tags" element={<TagManagement />} />

                    <Route path="plans" element={<PlanManagement />} />
                    <Route path="plans/:id" element={<PlanManagement mode="show" />} />

                    <Route path="lifepoints" element={<LifePoints />} />
                    <Route path="categories" element={<CategoryManagement />} />
                    
                    <Route path="scenarios" element={<ScenarioManagement />} />
                    <Route path="scenarios/:id" element={<ScenarioManagement mode="show" />} />
                    <Route path="onboardings" element={<OnboardingManagement />} />
                    <Route path="onboardings/:id" element={<OnboardingManagement mode="show" />} />

                    <Route path="rules" element={<RuleManagement />} />
                    <Route path="rules/create" element={<RuleManagement mode="create" />} />
                    <Route path="rules/:id" element={<RuleManagement mode="show" />} />
                    <Route path="rules/:id/edit" element={<RuleManagement mode="edit" />} />
                  </Route>

                  <Route path="sites/*" element={<LegacySitesRedirect />} />
                </Route>

                <Route path="analytics" element={<Reports />}>
                  <Route index element={<AnalyticsIndex><SubjectsTrailTable /></AnalyticsIndex>} />

                  <Route element={<RequireScreen fallback="/analytics" />}>
                    <Route path="monitoring/scenarios" element={<MessagesAnalyticsTable />} />
                    <Route path="monitoring/scenarios/:id" element={<MessagesAnalyticsTable mode="show" />} />
                    <Route path="monitoring/tasks" element={<TasksAnalyticsTable />} />
                    <Route path="monitoring/tasks/:id" element={<TasksAnalyticsTable mode="show" />} />
                    <Route path="monitoring/onboardings" element={<OnboardingsAnalyticsTable />} />
                    <Route path="monitoring/onboardings/:id" element={<OnboardingsAnalyticsTable mode="show" />} />
                    <Route path="audit-trail/subjects" element={<SubjectsTrailTable />} />
                    <Route path="audit-trail/scenarios" element={<ScenariosTrailTable />} />
                    <Route path="audit-trail/users" element={<UsersTrailTable />} />
                    <Route path="audit-trail/chats" element={<ChatsTrailTable />} />
                    <Route path="audit-trail/ai-calls" element={<AiCallsTable />} />
                    <Route path="monitoring/articles" element={<ArticleEngagementTable />} />
                    <Route path="monitoring/articles/:id" element={<ArticleEngagementTable mode="show" />} />
                    {/* Unified AI Assistant Routes */}
                    <Route path="ai-assistant" element={<UnifiedAiAssistant />} />
                    <Route path="ai-assistant/:sessionId" element={<UnifiedAiAssistant />} />
                    <Route path="reports/surveys" element={<SurveysReport />} />
                    <Route path="reports/onboardings" element={<OnboardingsReport />} />
                    <Route path="usage" element={<UsageAnalytics />} />
                  </Route>
                </Route>
              </Route>
            </Route>

          </Routes>
        </Router>
      </ConfigProvider>
    </I18nextProvider>
  </Provider>
);

export default App;
